import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { leaderboardPageText } from '../components/QuizPageText/quizPageText.data';
import { getPlayerLeaderboard, getGrandMasters, getReigns, formatTime, formatRelativeDate } from '../services/leaderboardService';
import { DEFAULT_AVATAR } from '../config/avatarConfig';
import Avatar from '../components/Avatar/Avatar';
import FeedbackForm from '../components/FeedbackForm/FeedbackForm';
import './Leaderboard.css';

const Leaderboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('character');
  const [entries, setEntries] = useState([]);
  const [grandMasters, setGrandMasters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  // The player whose runs are open under their row (lowercase name), if any.
  const [openPlayer, setOpenPlayer] = useState(null);
  const [reign, setReign] = useState(null);
  // Every load gets a number; only the newest one may write to the table.
  // Without this, switching tabs quickly let an older tab's reply land last
  // and fill the Character tab with Bounty rows.
  const latestLoad = useRef(0);

  const tabs = [
    { id: 'character', label: 'Character', icon: '/character-quiz-and-any-related.png' },
    { id: 'image', label: 'Image', icon: '/image-quiz-and-any-related.png' },
    { id: 'devilfruit', label: 'Devil Fruit', icon: '/devil-fruit-quiz-and-any-related.png' },
    { id: 'bounty', label: 'Bounty Battle', emoji: '💰' },
    { id: 'grand-masters', label: 'Grand Masters', icon: '/favonepie.png' },
  ];

  // Bounty Battle runs 11 matchups; the three quizzes ask 15 questions.
  const MAX_SCORE = { character: 15, image: 15, devilfruit: 15, bounty: 11 };

  // What each board is, shown above it so a newcomer knows what they are looking at.
  const GAME_DETAILS = {
    character: { name: 'Character Quiz', path: '/quiz', icon: '/character-quiz-and-any-related.png', what: 'One picture, four names. Who is it?', rules: '15 questions · ties go to the faster time' },
    image: { name: 'Image Quiz', path: '/image-quiz', icon: '/image-quiz-and-any-related.png', what: 'One name, four faces. Pick the right one.', rules: '15 questions · ties go to the faster time' },
    devilfruit: { name: 'Devil Fruit Quiz', path: '/devil-fruit-quiz', icon: '/devil-fruit-quiz-and-any-related.png', what: 'Name the user behind every Devil Fruit.', rules: '15 questions · ties go to the faster time' },
    bounty: { name: 'Bounty Battle', path: '/bounty-battle', emoji: '💰', what: 'Is the next bounty higher or lower?', rules: '11 matchups · ties go to the faster time' },
  };

  // Load data function - reusable for manual refresh
  const loadData = useCallback(async (showLoadingSpinner = true) => {
    const loadId = ++latestLoad.current;
    const isLatest = () => loadId === latestLoad.current;
    if (showLoadingSpinner) {
      setLoading(true);
    } else {
      setIsRefreshing(true);
    }

    try {
      if (activeTab === 'grand-masters') {
        const [data, reigns] = await Promise.all([getGrandMasters(), getReigns(activeTab)]);
        if (!isLatest()) return;
        setGrandMasters(data);
        setReign(reigns);
      } else {
        // The Grand Masters come along on every tab so their rows can be marked
        // in the ordinary tables too. Fetched in parallel, so this costs no
        // extra wait.
        const [data, masters, reigns] = await Promise.all([
          getPlayerLeaderboard(activeTab, 50),
          getGrandMasters(),
          getReigns(activeTab),
        ]);
        if (!isLatest()) return;
        setEntries(data);
        setReign(reigns);
        setGrandMasters(masters);
      }
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    }

    if (!isLatest()) return;
    setLoading(false);
    setIsRefreshing(false);
  }, [activeTab]);

  // Bring a player's runs into view when they open near the bottom of the table.
  useEffect(() => {
    if (!openPlayer) return;
    const panel = document.querySelector('.runs-panel');
    if (panel && panel.scrollIntoView) panel.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [openPlayer]);

  // Load data on tab change
  useEffect(() => {
    setOpenPlayer(null);
    setReign(null);
    loadData();
  }, [loadData]);

  // Refresh data when page becomes visible (user returns from quiz)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        loadData(false); // Refresh without full loading spinner
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadData]);

  // Refresh data when navigating to this page
  useEffect(() => {
    loadData(false);
  }, [location.key, loadData]);

  // Manual refresh handler
  const handleRefresh = () => {
    loadData(false);
  };

  const getRankDisplay = (rank) => {
    if (rank === 1) return <span className="rank-medal gold">🥇</span>;
    if (rank === 2) return <span className="rank-medal silver">🥈</span>;
    if (rank === 3) return <span className="rank-medal bronze">🥉</span>;
    return <span className="rank-number">{rank}</span>;
  };

  const getTabTitle = () => {
    const titles = {
      'character': 'Character Quiz Rankings',
      'image': 'Image Quiz Rankings',
      'devilfruit': 'Devil Fruit Quiz Rankings',
      'bounty': 'Bounty Battle Rankings',
      'grand-masters': 'Hall of Legends'
    };
    return titles[activeTab];
  };

  // Grand Masters, keyed by name, so an ordinary row can tell in one lookup
  // whether the player has cleared 11+ in all four games.
  const masterOf = (username) => grandMasters.find((gm) => gm.username === username);

  // The board's own numbers: who is on it, how often it is played, how recently.
  const renderGameDetails = () => {
    const g = GAME_DETAILS[activeTab];
    if (!g) return null;
    const runs = entries.flatMap((e) => (e.runs && e.runs.length ? e.runs : [e]));
    const last = runs.reduce((m, r) => (r.date && r.date > m ? r.date : m), '');
    const perfect = entries.filter((e) => e.score === MAX_SCORE[activeTab]).length;
    return (
      <div className="lb-game">
        <div className="lb-game-top">
          {g.emoji ? <span className="lb-game-icon lb-game-emoji">{g.emoji}</span> : <img src={g.icon} alt="" className="lb-game-icon" />}
          <div className="lb-game-text">
            <div className="lb-game-name">{g.name}</div>
            <div className="lb-game-what">{g.what}</div>
            <div className="lb-game-rules">{g.rules}</div>
          </div>
          <button type="button" className="lb-game-play" onClick={() => navigate(g.path)}>Play</button>
        </div>
        <div className="lb-game-stats">
          <div className="lb-stat"><b>{entries.length}</b><span>pirates</span></div>
          <div className="lb-stat"><b>{runs.length}</b><span>runs</span></div>
          <div className="lb-stat"><b>{perfect}</b><span>perfect</span></div>
          <div className="lb-stat"><b>{last ? formatRelativeDate(last) : '—'}</b><span>last played</span></div>
        </div>
      </div>
    );
  };

  // Top three on a podium, 2 · 1 · 3. Tapping one opens their runs in the list.
  const renderPodium = () => {
    if (entries.length < 3) return null;
    const order = [1, 0, 2];
    return (
      <div className="lb-podium">
        {order.map((i) => {
          const e = entries[i];
          const key = e.username.toLowerCase();
          return (
            <button key={e.id} type="button" className={`lb-spot lb-spot-${i + 1}`}
              onClick={() => { setOpenPlayer(key); const row = document.getElementById(`lb-row-${key}`); if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' }); }}>
              <span className="lb-spot-medal">{['🥇', '🥈', '🥉'][i]}</span>
              <Avatar value={e.avatar} className="lb-spot-avatar" alt="" />
              <span className="lb-spot-name">{e.username}</span>
              <span className="lb-spot-score">{e.score}<small>/{MAX_SCORE[activeTab] || 15}</small></span>
              <span className="lb-spot-time">{formatTime(e.totalTime)}</span>
              <span className="lb-spot-base">{i + 1}</span>
            </button>
          );
        })}
      </div>
    );
  };

  const renderRegularLeaderboard = () => (
    <div className="leaderboard-table">
      {/* Table Header */}
      <div className="table-header">
        <div className="col-rank">RANK</div>
        <div className="col-player">PIRATE</div>
        <div className="col-score">SCORE</div>
        <div className="col-time">TIME</div>
        <div className="col-date">DATE</div>
      </div>

      {/* Table Rows — one per player; tap a player with more than one run to see them all */}
      <div className="table-body">
        {entries.map((entry, index) => {
          const rank = index + 1;
          const key = entry.username.toLowerCase();
          const runCount = entry.runs ? entry.runs.length : 1;
          const canOpen = runCount > 1;
          const isOpen = canOpen && openPlayer === key;
          const toggle = () => canOpen && setOpenPlayer(isOpen ? null : key);
          return (
            <React.Fragment key={entry.id}>
              <div
                id={`lb-row-${key}`}
                className={`table-row ${rank <= 3 ? `top-${rank}` : ''} ${canOpen ? 'can-open' : ''} ${isOpen ? 'is-open' : ''}`}
                style={{ animationDelay: `${index * 0.05}s` }}
                onClick={toggle}
                onKeyDown={(e) => {
                  if (canOpen && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    toggle();
                  }
                }}
                role={canOpen ? 'button' : undefined}
                tabIndex={canOpen ? 0 : undefined}
                aria-expanded={canOpen ? isOpen : undefined}
                title={canOpen ? `${runCount} runs, tap to see them all` : undefined}
              >
                {/* Glow effect for top 3 */}
                {rank <= 3 && <div className={`row-glow glow-${rank}`}></div>}

                <div className="col-rank">
                  {getRankDisplay(rank)}
                </div>

                <div className="col-player">
                  <Avatar value={entry.avatar} className="player-avatar" alt="" />
                  <span className="player-info">
                  <span className={`player-name ${masterOf(entry.username) ? 'is-master' : ''}`}>
                    {rank === 1 && <span className="crown">👑</span>}
                    <span className="name-text">{entry.username}</span>
                    {masterOf(entry.username) && (
                      <span
                        className={`master-pill tier-${masterOf(entry.username).tier.toLowerCase().replace(' ', '-')}`}
                        title={`${masterOf(entry.username).tier} — ${masterOf(entry.username).totalScore}/56 across all four games`}
                      >
                        <span className="master-pill-icon">{masterOf(entry.username).tierIcon}</span>
                        <span className="master-pill-text">{masterOf(entry.username).tier}</span>
                      </span>
                    )}
                  </span>
                  {canOpen && (
                    <span className="runs-chip">
                      ×{runCount}
                      <span className="runs-chevron" aria-hidden="true">▾</span>
                    </span>
                  )}
                  </span>
                </div>

                <div className="col-score">
                  <span className="score-value">{entry.score}</span>
                  <span className="score-max">/{MAX_SCORE[activeTab] || 15}</span>
                </div>

                <div className="col-time">
                  {formatTime(entry.totalTime)}
                </div>

                <div className="col-date">
                  {formatRelativeDate(entry.date)}
                </div>
              </div>

              {isOpen && (
                <div className="runs-panel">
                  <div className="runs-panel-title">
                    {entry.username} · {runCount} runs
                  </div>
                  <div className="runs-grid">
                    {entry.runs.map((run, i) => (
                      <div key={run.id} className={`run-card ${i === 0 ? 'is-best' : ''}`}>
                        <span className="run-label">{i === 0 ? 'BEST' : `#${i + 1}`}</span>
                        <span className="run-score">
                          {run.score}<span className="run-max">/{MAX_SCORE[activeTab] || 15}</span>
                        </span>
                        <span className="run-meta">
                          {formatTime(run.totalTime)} · {formatRelativeDate(run.date)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );

  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  // "6 runs couldn't beat it" reads plainer than "held off 6 challengers".
  const beaten = (n) => (n === 0 ? 'no one has tried yet' : `${plural(n, 'run')} couldn't beat it`);
  const heldFor = (days) => (days < 1 ? 'since today' : `for ${plural(days, 'day')}`);

  // The reigning #1, worked out live from the plays (see computeReigns).
  const renderReign = () => {
    if (!reign) return null;
    const { current, longest, first } = reign;
    const currentIsLongest = longest.current;
    return (
      <div className="reign-strip">
        <div className="reign-main">
          <span className="reign-crown" aria-hidden="true">👑</span>
          <Avatar value={current.avatar} className="reign-avatar" alt="" />
          <span className="reign-name">{current.username}</span>
          <span className="reign-stats">
            <span className="reign-stat">#1 {heldFor(current.days)}</span>
            <span className="reign-stat">{beaten(current.challengers)}</span>
          </span>
        </div>
        <div className="reign-sub">
          {currentIsLongest ? (
            <span className="reign-fact reign-record">🏆 Longest time at #1 on this board</span>
          ) : (
            <span className="reign-fact">
              <span className="reign-chunk">Longest time at #1: <b>{longest.username}</b></span>
              {' · '}<span className="reign-chunk">{plural(longest.days, 'day')}</span>
              {' · '}<span className="reign-chunk">{beaten(longest.challengers)}</span>
            </span>
          )}
          <span className="reign-fact">First ever #1: <b>{first}</b></span>
        </div>
      </div>
    );
  };

  const renderGrandMasters = () => (
    <div className="grand-masters-container">
      {grandMasters.length === 0 ? (
        <div className="no-grand-masters">
          <div className="empty-icon">🏴‍☠️</div>
          <h3>No Grand Masters Yet</h3>
          <p>Score 11+ in ALL 4 games to become a legend!</p>
          <div className="requirements">
            <div className="requirement">
              <img src="/character-quiz-and-any-related.png" alt="Character" className="req-icon-img" />
              <span>Character Quiz: 11+</span>
            </div>
            <div className="requirement">
              <img src="/image-quiz-and-any-related.png" alt="" className="req-icon-img" />
              <span>Image Quiz: 11+</span>
            </div>
            <div className="requirement">
              <img src="/devil-fruit-quiz-and-any-related.png" alt="Devil Fruit" className="req-icon-img" />
              <span>Devil Fruit Quiz: 11+</span>
            </div>
            <div className="requirement">
              <span className="req-icon-img" style={{ fontSize: '1.3rem', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>💰</span>
              <span>Bounty Battle: 11+</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="grand-masters-list">
          {/* Legend for tiers */}
          <div className="tier-legend">
            {/* One title. The old three-tier text also quoted totals out of 45,
                which predates Bounty Battle — the four games top out at 56. */}
            <div className="tier-item pirate-king">
              <span className="tier-icon">🏴‍☠️</span>
              <span className="tier-name">PIRATE KING</span>
              <span className="tier-desc">11+ in all four games</span>
            </div>
          </div>

          {/* Grand Masters Table */}
          <div className="gm-table">
            <div className="gm-header">
              <div className="gm-col-rank">RANK</div>
              <div className="gm-col-tier">TIER</div>
              <div className="gm-col-player">PIRATE</div>
              <div className="gm-col-total">TOTAL</div>
              <div className="gm-col-breakdown">BREAKDOWN</div>
              <div className="gm-col-time" title="Best run in each game, times added together">TOTAL TIME</div>
              <div className="gm-col-date">ACHIEVED</div>
            </div>

            <div className="gm-body">
              {grandMasters.map((master, index) => {
                const rank = index + 1;
                return (
                  <div
                    key={master.username}
                    className={`gm-row tier-${master.tier.toLowerCase().replace(' ', '-')}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {/* Special glow for Pirate Kings */}
                    {master.tier === 'PIRATE KING' && (
                      <div className="pirate-king-glow"></div>
                    )}

                    <div className="gm-col-rank">
                      {getRankDisplay(rank)}
                    </div>

                    <div className="gm-col-tier">
                      <span className={`tier-badge tier-${master.tier.toLowerCase().replace(' ', '-')}`}>
                        <span className="badge-icon">{master.tierIcon}</span>
                        <span className="badge-text">{master.tier}</span>
                      </span>
                    </div>

                    <div className="gm-col-player">
                      <Avatar value={master.avatar} className="gm-player-avatar" alt="" />
                      <span className="gm-player-name">{master.username}</span>
                    </div>

                    <div className="gm-col-total">
                      <span className="total-score">{master.totalScore}</span>
                      <span className="total-max">/56</span>
                    </div>

                    <div className="gm-col-breakdown">
                      <span className="breakdown-item" title="Character Quiz">
                        <img src="/character-quiz-and-any-related.png" alt="Character" className="breakdown-icon" /> {master.characterScore}
                      </span>
                      <span className="breakdown-item" title="Image Quiz">
                        <img src="/image-quiz-and-any-related.png" alt="" className="breakdown-icon" /> {master.imageScore}
                      </span>
                      <span className="breakdown-item" title="Devil Fruit Quiz">
                        <img src="/devil-fruit-quiz-and-any-related.png" alt="Devil Fruit" className="breakdown-icon" /> {master.devilFruitScore}
                      </span>
                      <span className="breakdown-item" title="Bounty Battle">
                        <span className="breakdown-icon" style={{ display: 'inline-flex', alignItems: 'center' }}>💰</span> {master.bountyScore}
                      </span>
                    </div>

                    <div className="gm-col-time" title="Best run in each game, times added together">
                      {formatTime(master.totalTime)}
                    </div>

                    <div className="gm-col-date">
                      {formatRelativeDate(master.date)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        <title>One Piece Quiz Leaderboard - Top Scores | One Piece Trivia</title>
        <meta name="description" content="Top scores for the One Piece character quiz, image quiz, and Devil Fruit quiz. See where you rank and who the Grand Masters are." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/leaderboard" />
        <meta property="og:title" content="One Piece Quiz Leaderboard - Top Scores" />
        <meta property="og:description" content="Top scores across all One Piece quiz games. See where you rank." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/leaderboard" />
        <meta name="twitter:title" content="One Piece Quiz Leaderboard - Top Scores" />
        <meta name="twitter:description" content="Top scores across all One Piece quiz games." />
      </Helmet>
      <div className="leaderboard-container">
        {/* Animated background particles */}
        <div className="particles-container">
          {[...Array(20)].map((_, i) => (
            <div key={i} className={`particle particle-${i % 5}`} />
          ))}
        </div>

        {/* Header */}
        <div className="leaderboard-header">
        <div className="header-glow"></div>
        <h1 className="leaderboard-title">
          <span className="title-icon">⚔️</span>
          PIRATE RANKINGS
          <span className="title-icon">⚔️</span>
        </h1>
        <p className="leaderboard-subtitle">The Greatest One Piece Trivia Masters</p>
      </div>

      {/* Tab Navigation */}
      <div className="tabs-container">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.id === 'grand-masters' ? 'grand-masters-tab' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.emoji
              ? <span className="tab-icon-img" style={{ fontSize: '1.4rem', lineHeight: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{tab.emoji}</span>
              : <img src={tab.icon} alt={tab.label} className="tab-icon-img" />}
            <span className="tab-label">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Title */}
      <div className="tab-title-container">
        <div className="tab-title-row">
          <h2 className="tab-title">{getTabTitle()}</h2>
          <button
            className={`refresh-btn ${isRefreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={loading || isRefreshing}
            title="Refresh leaderboard"
          >
            <span className="refresh-glyph" aria-hidden="true">↻</span>
          </button>
        </div>
        {activeTab !== 'grand-masters' && (
          <span className="tab-subtitle">Top 50 Players · tap a player to see every run</span>
        )}
        {activeTab === 'grand-masters' && (
          <span className="tab-subtitle">Players with 11+ in ALL 4 games</span>
        )}
      </div>

      {/* Leaderboard Content */}
      <div className="leaderboard-table-container">
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading rankings...</p>
          </div>
        ) : (
          <>
            {activeTab !== 'grand-masters' && renderGameDetails()}
            {activeTab !== 'grand-masters' && renderPodium()}
            {renderReign()}
            {activeTab === 'grand-masters' ? renderGrandMasters() : renderRegularLeaderboard()}
          </>
        )}
      </div>

      {/* Bottom Actions */}
      <div className="leaderboard-actions">
        <button className="action-btn home-btn" onClick={() => navigate('/')}>
          <span className="btn-icon">🏠</span>
          <span className="btn-text">Home</span>
        </button>
        <button className="action-btn play-btn" onClick={() => navigate('/quiz')}>
          <span className="btn-icon">🎮</span>
          <span className="btn-text">Play Now</span>
        </button>
      </div>

      <FeedbackForm />

        {/* Footer decoration */}
        <div className="leaderboard-footer-decoration">
          <div className="decoration-line"></div>
          <span className="decoration-text">Become the Pirate King of Trivia!</span>
          <div className="decoration-line"></div>
        </div>
      </div>
      <QuizPageText {...leaderboardPageText} />
    </>
  );
};

export default Leaderboard;
